import { useCallback, useEffect, useMemo, useState } from "react";
import notificationsCenter from "../../../singleton/NotificationsCenter";
import { LogMessageType } from "../../../utils/messages";
import { DEFAULT_ASSETS } from "../utils/defaultAssets";
import { setApiKey, fetchAssets as fetchLibrariesAssets, fetchCategories } from "../utils/librariesApi";
import { getAuthHeaders } from "../../../utils/getToken";
import { normalizeWorkspaceAssetUpdate } from "../../../utils/workspace-asset-registration";

const fileTypeOptions = [
  { key: "all", text: "All" },
  { key: "model", text: "3D Model" },
  { key: "image", text: "Image" },
  { key: "video", text: "Video" },
  { key: "audio", text: "Audio" },
  { key: "gif", text: "GIF" },
  { key: "pdf", text: "PDF" }
];

const assetCategoryOptions = [
  { key: "all", text: "All Categories" },
  { key: "file", text: "File" },
  { key: "digital-art", text: "Digital Art" },
  { key: "digital-twin-art", text: "Digital Twin Art" }
];

const sourceOptions = [
  { key: "nga", text: "National Gallery of Art" },
  { key: "met", text: "The Metropolitan Museum of Art" }
];

const sourceOptionsLibraries = [{ key: "sketchfab", text: "Sketchfab" }];

const tabs = [
  { value: "files", label: "Files" },
  { value: "public-domain-art", label: "Public Domain Art" },
  { value: "libraries", label: "Libraries" }
];

const extractCursor = url => {
  if (!url) return null;

  const urlObj = new URL(url);
  return urlObj.searchParams.get("cursor") || urlObj.searchParams.get("page");
};

const PER_PAGE = 12;

const normalizeBaseUrl = baseUrl => String(baseUrl || "").replace(/\/+$/, "");

const formatPublicArtEntry = artwork => {
  const thumbnail = artwork.primaryImageSmall || artwork.primaryImage || artwork.image || artwork.url || null;
  const entryUrl = artwork.url || artwork.primaryImage || artwork.image || thumbnail || null;
  const artworkId = artwork.objectID ?? artwork.id ?? null;
  const title = artwork.title ?? artwork.name;

  return {
    ...artwork,
    attributions: {
      creator: {
        name: artwork.artistDisplayName ?? ""
      }
    },
    author: artwork.artistDisplayName ?? "",
    artworkDate: artwork.objectDate,
    collection: artwork.department,
    id: artworkId,
    isCommunityMedia: true,
    thumbnail,
    images: {
      preview: {
        url: thumbnail
      }
    },
    public_thumbnail: thumbnail,
    title,
    name: title,
    type: "artwork",
    artwork: {
      id: artworkId,
      artist: artwork.artistDisplayName ?? "",
      author: artwork.artistDisplayName ?? "",
      dimensions: artwork.dimensions,
      category: artwork.department,
      museum: artwork.museum,
      title,
      medium: artwork.medium
    },
    url: entryUrl
  };
};

const fetchPublicArtDepartments = async ({ baseUrl, museum, signal }) => {
  const response = await fetch(`${baseUrl}/${museum}/departments`, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${museum} departments`);
  }
  const payload = await response.json();
  return Array.isArray(payload?.data) ? payload.data : [];
};

const fetchPublicArtList = async ({ baseUrl, museum, page, query, department, signal }) => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(PER_PAGE)
  });

  if (query) {
    params.set("q", query);
  }

  if (department && department !== "all") {
    params.set("department", department);
  }

  const response = await fetch(`${baseUrl}/${museum}?${params.toString()}`, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${museum} artworks`);
  }

  const payload = await response.json();
  const data = Array.isArray(payload?.data) ? payload.data : [];
  const total = Number(payload?.meta?.total || 0);
  const totalPages = Math.ceil(total / PER_PAGE);

  const entries = data
    .map(formatPublicArtEntry)
    .filter(entry => entry?.id !== null && entry?.id !== undefined)
    .filter((entry, index, self) => index === self.findIndex(t => t.id === entry.id));

  return {
    entries,
    meta: {
      next_page: totalPages > page ? page + 1 : null,
      prev_page: page > 1 ? page - 1 : null
    }
  };
};

export function useFetchAssets({
  assetsViewEndpoint,
  assetsEndpoint,
  publicDomainArtEndpoint,
  librariesApiKey,
  defaultToken = null,
  signedIn
}) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [assetCategoryFilter, setAssetCategoryFilter] = useState("all");
  const [selectedTab, setSelectedTab] = useState(signedIn ? "files" : "public-domain-art");
  // Set this to false to skip the first fetch under development
  const [loaded, setLoaded] = useState(true);
  const [collections, setCollections] = useState({});
  const [source, setSource] = useState("nga");
  const [nextCursor, setNextCursor] = useState(null);
  const [prevCursor, setPrevCursor] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  const onChangePlaceLoading = useCallback(
    value => {
      setPlaceLoading(value);
    },
    [setPlaceLoading]
  );

  const hasNext = useMemo(() => !!nextCursor || !!nextPage, [nextCursor, nextPage]);
  const hasPrev = useMemo(() => prevCursor !== null || !!prevPage, [prevCursor, prevPage]);
  const editable = useMemo(() => selectedTab === tabs[0].value, [selectedTab]);

  const onNext = useCallback(() => {
    setPage(nextPage);
    setCursor(nextCursor);
  }, [nextCursor, nextPage]);

  const onPrev = useCallback(() => {
    setPage(prevPage);
    setCursor(prevCursor);
  }, [prevCursor, prevPage]);

  // Create a computed variable that will define to show fileTypeOptions, collections.nga or collections.met
  const options = useMemo(() => {
    switch (selectedTab) {
      case "files":
        return fileTypeOptions;
      case "public-domain-art":
        return source === "met" ? collections.met : collections.nga;
      case "libraries":
        return collections.libraries;
      default:
        return [];
    }
  }, [selectedTab, collections.nga, collections.met, collections.libraries, source]);

  const formatCollections = useCallback(collections => {
    // Add the "All" option to the beginning of the collections array
    const allOption = { key: "all", text: "All" };

    const formattedCollections = collections.map(collection => {
      return {
        key: collection.id || collection.slug || collection.name,
        text: collection.name
      };
    });

    return [allOption, ...formattedCollections];
  }, []);

  useEffect(() => {
    setApiKey(librariesApiKey);
  }, [librariesApiKey]);

  useEffect(() => {
    const loadCollections = async () => {
      const baseUrl = normalizeBaseUrl(publicDomainArtEndpoint);

      const [ngaCollections, metCollections] = await Promise.all([
        fetchPublicArtDepartments({ baseUrl, museum: "nga" }).catch(() => []),
        fetchPublicArtDepartments({ baseUrl, museum: "met" }).catch(() => [])
      ]);
      const librariesCollections = await fetchCategories();

      setCollections(collections => ({
        ...collections,
        nga: formatCollections(ngaCollections),
        met: formatCollections(metCollections),
        libraries: formatCollections(librariesCollections)
      }));
    };

    loadCollections();
  }, [formatCollections, publicDomainArtEndpoint]);

  // Create a function to rename, it will call the API with the asset id and the new name
  const renameAsset = useCallback(
    async ({ id, title }) => {
      const assetsUrl = assetsEndpoint + "/" + id;
      const authHeaders = await getAuthHeaders(assetsUrl, "PATCH");

      if (!authHeaders.Authorization) {
        setError("No token");
        return;
      }

      try {
        const response = await fetch(assetsUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders
          },
          body: JSON.stringify({ title })
        });

        if (!response.ok) {
          throw new Error("Failed to rename asset");
        }
        await response.json();
        setAssets(assets.map(asset => (asset.id === id ? { ...asset, title } : asset)));

        notificationsCenter.success(LogMessageType.assetRenamed);
      } catch (err) {
        setError(err.message);
        notificationsCenter.error(LogMessageType.assetRenameFailed);
      }
    },
    [assetsEndpoint, assets]
  );

  const deleteAsset = useCallback(
    async ({ id }) => {
      const assetsUrl = assetsEndpoint + "/" + id;
      const authHeaders = await getAuthHeaders(assetsUrl, "DELETE");

      if (!authHeaders.Authorization) {
        setError("No token");
        return;
      }

      try {
        const response = await fetch(assetsUrl, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders
          }
        });

        if (!response.ok) {
          throw new Error("Failed to delete asset");
        }

        setAssets(assets.filter(asset => asset.id !== id));
        notificationsCenter.success(LogMessageType.assetDeleted);
      } catch (err) {
        setError(err.message);
        notificationsCenter.error(LogMessageType.assetDeleteFailed);
      }
    },
    [assets, assetsEndpoint]
  );

  const upsertAsset = useCallback((updatedAsset, options = {}) => {
    if (!updatedAsset) {
      return;
    }

    const { prepend = false } = options;
    setAssets(prevAssets => {
      if (!Array.isArray(prevAssets)) {
        return prevAssets;
      }

      const assetExists = prevAssets.some(asset => asset.id === updatedAsset.id);

      if (assetExists) {
        return prevAssets.map(asset => (asset.id === updatedAsset.id ? { ...asset, ...updatedAsset } : asset));
      }

      return prepend ? [updatedAsset, ...prevAssets] : [...prevAssets, updatedAsset];
    });
  }, []);

  const editAsset = useCallback(
    async ({ id, updatedData }) => {
      const assetsUrl = assetsEndpoint + "/" + id;
      const authHeaders = await getAuthHeaders(assetsUrl, "PATCH");

      if (!authHeaders.Authorization) {
        setError("No token");
        return;
      }

      try {
        const response = await fetch(assetsUrl, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders
          },
          body: JSON.stringify(normalizeWorkspaceAssetUpdate(updatedData))
        });

        if (!response.ok) {
          throw new Error("Failed to update asset");
        }

        const responseData = await response.json();
        setAssets(
          assets.map(asset => (asset.id === id ? { ...asset, ...(responseData.data || responseData) } : asset))
        );

        notificationsCenter.success(LogMessageType.assetUpdated);
      } catch (err) {
        setError(err.message);
        notificationsCenter.error(LogMessageType.assetUpdateFailed);
      }
    },
    [assets, assetsEndpoint]
  );

  const handleFileTypeFilterChange = useCallback(
    (_, data) => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setPrevCursor(null);
      setSearchQuery("");
      setFileTypeFilter(data.optionValue);
    },
    [setFileTypeFilter]
  );

  const handleAssetCategoryFilterChange = useCallback(
    (_, data) => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setPrevCursor(null);
      setSearchQuery("");
      setAssetCategoryFilter(data.optionValue);
    },
    [setAssetCategoryFilter]
  );

  const handleSourceChange = useCallback(
    (_, data) => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setPrevCursor(null);
      setSearchQuery("");
      setFileTypeFilter("all");
      setSource(data.optionValue);
    },
    [setSource]
  );

  const handleSelectedTabChange = useCallback(
    value => {
      setCursor(0);
      setPage(1);
      setNextPage(null);
      setPrevPage(null);
      setNextCursor(null);
      setSearchQuery("");
      setFileTypeFilter("all");
      setAssetCategoryFilter("all");
      setSource(value === "libraries" ? "sketchfab" : "nga");
      setSelectedTab(value);
    },
    [setSelectedTab]
  );

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    // Reset error state at the beginning of the effect
    setError(null);

    async function fetchAssets() {
      setLoading(true);
      try {
        let assetsUrl = assetsViewEndpoint;
        assetsUrl = `${assetsUrl}?asset_type=my-media&source=metaverse`;

        if (debouncedQuery) {
          assetsUrl = `${assetsUrl}&q=${debouncedQuery}`;
        }

        if (fileTypeFilter !== "all") {
          assetsUrl = `${assetsUrl}&type=${fileTypeFilter}`;
        }

        if (assetCategoryFilter !== "all") {
          assetsUrl = `${assetsUrl}&asset_category=${assetCategoryFilter}`;
        }

        assetsUrl = `${assetsUrl}&per_page=${PER_PAGE}`;

        if (page) {
          assetsUrl = `${assetsUrl}&page=${page}`;
        }

        const authHeaders = await getAuthHeaders(assetsUrl, "GET");

        if (!authHeaders.Authorization) {
          setError("No token");
          return;
        }

        const response = await fetch(assetsUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders
          },
          signal
        });

        if (!response.ok) {
          throw new Error("Failed to fetch assets");
        }

        const data = await response.json();
        setAssets(data.data);
        setNextPage(extractCursor(data.links.next));
        setPrevPage(extractCursor(data.links.prev));
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    }

    async function fetchPublicArt() {
      try {
        setLoading(true);
        const baseUrl = normalizeBaseUrl(publicDomainArtEndpoint);
        const museum = source === "met" ? "met" : "nga";
        const result = await fetchPublicArtList({
          baseUrl,
          museum,
          page,
          query: debouncedQuery,
          department: fileTypeFilter === "all" ? null : fileTypeFilter,
          signal
        });
        setNextPage(result.meta.next_page);
        setPrevPage(result.meta.prev_page);
        setAssets(result.entries);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setLoading(false);
          setError(err.message);
        }
      }
    }

    async function fetchLibraries() {
      try {
        setLoading(true);
        const data = await fetchLibrariesAssets(
          fileTypeFilter === "all" ? null : fileTypeFilter,
          cursor,
          debouncedQuery,
          signal
        );
        setNextCursor(data[1].meta.next_cursor);
        setPrevCursor(data[1].meta.prev_cursor);
        setAssets(data[1].entries);
        setLoading(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    if (selectedTab === "files") {
      if (loaded) {
        fetchAssets();
      } else {
        // Use this under development only to skip the first fetch
        setAssets(DEFAULT_ASSETS);
        setLoading(false);
        setLoaded(true);
      }
    } else if (selectedTab === "public-domain-art") {
      fetchPublicArt();
    } else if (selectedTab === "libraries") {
      fetchLibraries();
    }

    return () => {
      controller.abort();
    };
  }, [
    debouncedQuery,
    fileTypeFilter,
    assetCategoryFilter,
    assetsViewEndpoint,
    assetsEndpoint,
    publicDomainArtEndpoint,
    defaultToken,
    selectedTab,
    source,
    cursor,
    page,
    loaded,
    shouldRefetch
  ]);

  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  const filteredTabs = useMemo(() => {
    if (!signedIn) {
      return tabs.filter(tab => tab.value !== "files");
    }
    return tabs;
  }, [signedIn]);

  return {
    assets,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fileTypeFilter,
    handleFileTypeFilterChange,
    fileTypeOptions: options,
    assetCategoryFilter,
    handleAssetCategoryFilterChange,
    assetCategoryOptions,
    renameAsset,
    deleteAsset,
    editAsset,
    tabs: filteredTabs,
    selectedTab,
    handleSelectedTabChange,
    sourceOptions:
      selectedTab === "public-domain-art" ? sourceOptions : selectedTab === "libraries" ? sourceOptionsLibraries : [],
    handleSourceChange,
    source,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    editable,
    onChangePlaceLoading,
    placeLoading,
    upsertAsset,
    refetch: () => setShouldRefetch(true)
  };
}
